import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { IonicModule, ModalController, NavController, AlertController } from '@ionic/angular';
import _ from 'lodash';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { ImageModalComponent } from 'src/app/components/image-modal/image-modal.component';
import { PROJECT_MEMBER_ROLES } from 'src/app/constants';
import { fileNameFromImageModalUpload } from 'src/app/utils/utils';
import { Tenant, SlackChannel } from 'src/models';
import { Portal, PortalFormModel } from 'src/models/portals';
import { Project, ProjectMember } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { PortalsService } from 'src/services/portals.service';
import { ProjectsService } from 'src/services/projects.service';
import { SlackService } from 'src/services/slack.service';
import { TenantsService } from 'src/services/tenants.service';
import { TicketTypesService } from 'src/services/ticket-types.service';
import { UploadService } from 'src/services/upload.service';

export interface PortalConfig {
  isExternal: boolean;
  domainSuffix: string;
  title: string;
  noPortalMessage: string;
  noPortalDescription: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, SharedPipesModule, NgxTippyModule, ChannelSelectorModule],
  selector: 'app-portals-base',
  templateUrl: './portals-base.component.html',
  styleUrls: ['./portals-base.component.scss'],
})
export class PortalsBaseComponent implements OnInit {
  @Input() config!: PortalConfig;

  portal: Partial<Portal> | null = null;
  projects: Partial<Project>[] = [];
  isCreatingPortal: boolean = false;
  tenant: Tenant | null = null;
  selectedSlackChannel: SlackChannel | null = null;

  // Tooltip content
  tooltips = {
    customDomain: `The domain where your portal will be accessible (e.g., support.${this.tenant?.subdomain || 'yourcompany'}.com). When disabled, a preview URL will be available for sharing with colleagues.`,
    logo: 'The logo that will be used for branding the customer portal',
    status:
      'Live portals are accessible to customers or employees; draft portals can only be seen by admins and can be shared via preview links',
    primaryColor: 'The main color theme used throughout the portal interface',
    projects:
      'Knowledge bases and other resources associated with these projects will be accessible via this customer portal',
    allowDirectTicketCreation: 'Allow users to create support tickets via a form directly from the portal',
    slackChannelId: 'The Slack channel ID where tickets created from this portal will be sent',
  };

  portalForm: PortalFormModel = {
    customDomain: '',
    primaryColor: '#000000',
    selectedProjectIds: [],
    supportLogo: null,
    isEnabled: false,
    allowDirectTicketCreation: true,
    slackChannelId: '',
  };

  // Track original form state for change detection
  originalPortalForm: PortalFormModel | null = null;
  originalSlackChannel: SlackChannel | null = null;

  // Track if custom domain has been changed from original
  get hasCustomDomainChanged(): boolean {
    if (!this.originalPortalForm) return false;
    return this.portalForm.customDomain !== this.originalPortalForm.customDomain;
  }

  // Track projects without ticket types for warning display
  projectsWithoutTicketTypes: { id: string; name: string }[] = [];

  isLoading = false;
  isInitialLoading = true; // Track initial loading state
  isUploadingLogo = false; // Track logo upload loading state
  isDnsTesting = false; // Track DNS test loading state
  isTlsTesting = false; // Track TLS test loading state
  isPublishing = false; // Track portal publishing loading state
  dnsTestSteps = [
    'Resolving domain...',
    'Checking CNAME records...',
    'Verifying DNS configuration...',
    'Testing connection...',
  ];
  tlsTestSteps = ['Checking certificate status...', 'Verifying SSL configuration...', 'Validating certificate...'];
  publishSteps = [
    'Checking ticket types...',
    'Validating project configuration...',
    'Verifying portal requirements...',
    'Testing DNS configuration...',
    'Checking TLS certificate...',
    'Preparing to publish...',
  ];
  currentDnsStep = 0;
  currentTlsStep = 0;
  currentPublishStep = 0;
  currentTestingPortal: Portal | null = null;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private portalsService: PortalsService,
    private msgSrvc: MessageService,
    private projectsService: ProjectsService,
    private tenantsService: TenantsService,
    private uploadService: UploadService,
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private alertCtrl: AlertController,
    private ticketTypesService: TicketTypesService,
    private slackService: SlackService,
  ) {}

  async ngOnInit() {
    await Promise.all([this.getProjects(), this.getTenant()]);

    // get portal data after other data is loaded
    await this.getPortal();

    // Automatically start portal creation if no portal exists
    // Only do this after initial loading is complete
    if (!this.portal) {
      await this.startCreatePortal();
    }
  }

  async getTenant() {
    try {
      this.tenant = await this.tenantsService.getTenant();
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  onSlackChannelChanged(channel: SlackChannel | undefined) {
    this.selectedSlackChannel = channel || null;
    this.portalForm.slackChannelId = channel?.id || '';
  }

  async getPortal() {
    try {
      const portalResponse = await this.portalsService.list({
        limit: null,
        descending: false,
        where: [
          {
            field: 'isExternal',
            operator: '==',
            value: this.config.isExternal,
          },
        ],
      });

      this.portal = portalResponse.data.length > 0 ? portalResponse.data[0] : null;

      // Initialize form with existing portal data if portal exists
      if (this.portal) {
        await this.initializePortalForm(this.portal);
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isInitialLoading = false;
    }
  }

  private async initializePortalForm(portal: Partial<Portal>) {
    // Create a new form object to ensure change detection picks up the updates
    const newForm: PortalFormModel = {
      customDomain: portal.customDomain || '',
      primaryColor: portal.settings?.primaryColor || '#000000',
      selectedProjectIds: [...(portal.projectIds || [])], // Create a copy of the array
      supportLogo: portal.settings?.logoUrl || null,
      isEnabled: portal.isEnabled || false,
      allowDirectTicketCreation: portal.settings?.allowDirectTicketCreation ?? true,
      slackChannelId: portal.slackChannelId || '',
    };

    // Update the form with the new data
    this.portalForm = newForm;

    // Set the selected Slack channel if we have one
    if (newForm.slackChannelId) {
      // Backend now provides slackChannel details through the association
      // If no channel details are available, create a minimal object
      if (!this.portal?.slackChannel) {
        this.selectedSlackChannel = {
          id: newForm.slackChannelId,
          name: 'Channel not found',
          displayName: 'Channel not found',
        } as SlackChannel;
      } else {
        this.selectedSlackChannel = this.portal.slackChannel;
        this.originalSlackChannel = this.portal.slackChannel;
      }
    }

    // Store the original state for change detection - create deep copies of arrays
    this.originalPortalForm = {
      ...newForm,
      selectedProjectIds: [...newForm.selectedProjectIds], // Create a copy of the array
    };

    // Initialize projects without ticket types warning for ALL projects, not just selected ones
    if (this.projects.length > 0) {
      const allProjectIds = this.projects.map((project) => project.id || '').filter((id) => id);
      this.projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(allProjectIds);
    }
  }

  // Check if the form has changed from its original state
  get hasFormChanges(): boolean {
    if (!this.originalPortalForm) return false;

    // Create copies of the arrays before sorting to avoid mutating the original arrays
    const currentProjectIds = [...this.portalForm.selectedProjectIds].sort();
    const originalProjectIds = [...this.originalPortalForm.selectedProjectIds].sort();

    return (
      this.portalForm.customDomain !== this.originalPortalForm.customDomain ||
      this.portalForm.primaryColor !== this.originalPortalForm.primaryColor ||
      JSON.stringify(currentProjectIds) !== JSON.stringify(originalProjectIds) ||
      this.portalForm.supportLogo !== this.originalPortalForm.supportLogo ||
      this.portalForm.isEnabled !== this.originalPortalForm.isEnabled ||
      this.portalForm.allowDirectTicketCreation !== this.originalPortalForm.allowDirectTicketCreation ||
      this.portalForm.slackChannelId !== this.originalPortalForm.slackChannelId
    );
  }

  // Restore the original form state
  restoreOriginalForm() {
    if (this.originalPortalForm) {
      this.portalForm = {
        ...this.originalPortalForm,
        selectedProjectIds: [...this.originalPortalForm.selectedProjectIds], // Create a copy of the array
      };

      this.selectedSlackChannel = this.originalSlackChannel;
    }
  }

  async getProjects() {
    try {
      if (this.authService.isTenantAdmin) {
        const projectsResponse = await this.projectsService.list({
          where: [
            {
              field: 'tenantId',
              operator: '==',
              value: this.authService.tenantId,
            },
            // Filter projects based on portal type
            {
              field: 'isExternal',
              operator: '==',
              value: this.config.isExternal,
            },
            // Filter out deleted projects
            {
              field: 'deletedAt',
              operator: '==',
              value: null,
            },
          ],
        });

        this.projects = projectsResponse.data;
      } else {
        const projectsResponse = await this.projectsService.listForUser(this.authService.userId, {
          limit: null,
          order: ['project.name'],
          descending: false,
          where: [
            {
              field: 'role',
              operator: '==',
              value: PROJECT_MEMBER_ROLES.OWNER,
            },
            // Filter projects based on portal type
            {
              field: 'project.isExternal',
              operator: '==',
              value: this.config.isExternal,
            },
            // Filter out deleted projects
            {
              field: 'project.deletedAt',
              operator: '==',
              value: null,
            },
          ],
        });

        this.projects = projectsResponse.data.map(
          (item: Partial<ProjectMember> & { project?: Project }) => item.project!,
        );
      }

      // Check for projects without ticket types when projects are loaded
      if (this.projects.length > 0) {
        const allProjectIds = this.projects.map((project) => project.id || '').filter((id) => id);
        this.projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(allProjectIds);
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async startCreatePortal() {
    this.isCreatingPortal = true;

    // Get default logo from tenant (supportLogo takes precedence over logo)
    const defaultLogo = this.tenant?.supportLogo || this.tenant?.logo || null;

    // Get default primary color from tenant
    const defaultPrimaryColor = this.tenant?.primaryColor || '#000000';

    // Get all project IDs for the current portal type
    const allProjectIds = this.projects.map((project) => project.id || '').filter((id) => id);

    // Initialize projects without ticket types warning first
    this.projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(allProjectIds);

    // Only select projects that have ticket types
    const projectsWithTicketTypes = allProjectIds.filter(
      (projectId) => !this.projectsWithoutTicketTypes.some((p) => p.id === projectId),
    );

    this.portalForm = {
      customDomain: '',
      primaryColor: defaultPrimaryColor,
      selectedProjectIds: projectsWithTicketTypes,
      supportLogo: defaultLogo,
      isEnabled: false,
      allowDirectTicketCreation: true,
      slackChannelId: '',
    };
    // Store the original state for change detection - create deep copies of arrays
    this.originalPortalForm = {
      ...this.portalForm,
      selectedProjectIds: [...this.portalForm.selectedProjectIds], // Create a copy of the array
    };
  }

  async cancelPortalForm() {
    this.isCreatingPortal = false;

    // Restore the original form state
    this.restoreOriginalForm();

    // If there's no portal, automatically start portal creation again
    if (!this.portal) {
      await this.startCreatePortal();
    }
  }

  async submitPortalForm() {
    // Validate domain only if provided
    if (this.portalForm.customDomain) {
      const domainError = this.validateDomain(this.portalForm.customDomain);
      if (domainError) {
        this.msgSrvc.showError(domainError);
        return;
      }
    }
    // Validate logo is required
    if (!this.portalForm.supportLogo) {
      this.msgSrvc.showError('Please upload a logo for your portal');
      return;
    }
    if (!this.portalForm.selectedProjectIds.length) {
      this.msgSrvc.showError('Please select at least one project');
      return;
    }
    // Validate Slack channel ID is required
    if (!this.portalForm.slackChannelId || this.portalForm.slackChannelId.trim() === '') {
      this.msgSrvc.showError('Please select a Slack channel');
      return;
    }

    // Validate that a Slack channel is selected
    if (!this.selectedSlackChannel) {
      this.msgSrvc.showError('Please select a Slack channel');
      return;
    }

    // Validate that ALL enabled projects have at least one ticket type before creating/updating portal
    const projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(this.portalForm.selectedProjectIds);
    if (projectsWithoutTicketTypes.length > 0) {
      const projectList = projectsWithoutTicketTypes
        .map((project) => `<li><strong>${project.name}</strong></li>`)
        .join('');

      const alert = await this.alertCtrl.create({
        header: 'Cannot Create Portal',
        message: `
          <div style="text-align: left;">
            <p><strong>All enabled projects must have at least one ticket type before you can create or update the portal.</strong></p>
            <p>The following projects don't have any ticket types:</p>
            <ul style="margin: 8px 0; padding-left: 20px;">
              ${projectList}
            </ul>
            <p>Please create ticket types for these projects first, then try again.</p>
          </div>
        `,
        buttons: [
          {
            text: 'OK',
            role: 'cancel',
          },
          {
            text: 'Go to Ticket Types',
            handler: () => {
              // Navigate to the first project's ticket types page
              if (projectsWithoutTicketTypes.length > 0) {
                this.navigateToTicketTypes(projectsWithoutTicketTypes[0].id);
              }
            },
          },
        ],
      });
      await alert.present();
      return;
    }

    this.isLoading = true;
    try {
      const portalData: any = {
        isExternal: this.config.isExternal,
        isEnabled: this.portalForm.isEnabled,
        projectIds: this.portalForm.selectedProjectIds,
        settings: {
          logoUrl: this.portalForm.supportLogo || '',
          primaryColor: this.portalForm.primaryColor,
          allowDirectTicketCreation: this.portalForm.allowDirectTicketCreation,
        },
        slackChannelId: this.portalForm.slackChannelId,
      };

      // Only include customDomain if it has a value
      if (this.portalForm.customDomain && this.portalForm.customDomain.trim()) {
        portalData.customDomain = this.portalForm.customDomain.trim();
      }

      if (this.portal) {
        const updatedPortal = await this.portalsService.update(this.portal.id, portalData);
        this.portal = updatedPortal;
        this.msgSrvc.show('Portal updated successfully');
        // Re-initialize the form with the updated portal data from the server
        await this.initializePortalForm(updatedPortal);
        // Force form object reference change
        this.portalForm = { ...this.portalForm };
      } else {
        // If no portal exists, create a new one
        const newPortal = await this.portalsService.create(portalData);
        this.portal = newPortal;
        this.isCreatingPortal = false; // Exit creation mode
        this.msgSrvc.show('Portal created successfully');
        // Refresh portal data to ensure we have the latest information
        await this.getPortal();
      }
      // Don't call cancelPortalForm here as we want to keep the current state
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  // Shared logo upload logic
  async uploadPortalLogo() {
    try {
      const modal = await this.modalCtrl.create({
        component: ImageModalComponent,
        componentProps: {
          title: 'Upload Logo',
          description: 'Upload a logo for your portal',
          aspectRatio: 1,
          maxWidth: 512,
          maxHeight: 512,
        },
      });
      await modal.present();
      const { data } = await modal.onWillDismiss();
      if (data && data.uploadFile) {
        this.isUploadingLogo = true;
        try {
          const uploadResult = await this.uploadService.upload(
            [data.uploadFile],
            'portal-logos',
            fileNameFromImageModalUpload(data.uploadFile),
          );
          this.portalForm.supportLogo = uploadResult[0].url;
        } catch (uploadError) {
          throw uploadError;
        } finally {
          this.isUploadingLogo = false;
        }
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  getProjectName(projectId: string): string {
    const project = this.projects.find((p) => p.id === projectId);
    return project?.name || 'Unknown Project';
  }

  // Check if there are no projects for the current portal type
  get hasNoProjectsForPortalType(): boolean {
    return this.projects.length === 0;
  }

  // Get the project type name for the current portal
  get projectTypeName(): string {
    return this.config.isExternal ? 'external' : 'internal';
  }

  // Make window available in template
  get window(): Window {
    return window;
  }

  // Validate that selected projects have at least one ticket type
  async validateProjectsHaveTicketTypes(projectIds: string[]): Promise<boolean> {
    if (!projectIds || projectIds.length === 0) {
      return false;
    }

    try {
      // Check each project for ticket types
      for (const projectId of projectIds) {
        const ticketTypesResponse = await this.ticketTypesService.list({
          limit: 1, // We only need to know if there's at least one
          where: [
            {
              field: 'projectId',
              operator: '==',
              value: projectId,
            },
          ],
        });

        if (ticketTypesResponse.data.length > 0) {
          // Found at least one ticket type for one of the projects
          return true;
        }
      }

      // No ticket types found for any of the selected projects
      return false;
    } catch (error) {
      // If there's an error, we'll assume validation passes to avoid blocking users
      return true;
    }
  }

  // Check which projects don't have ticket types and return project details
  async getProjectsWithoutTicketTypes(projectIds: string[]): Promise<{ id: string; name: string }[]> {
    if (!projectIds || projectIds.length === 0) {
      return [];
    }

    const projectsWithoutTicketTypes: { id: string; name: string }[] = [];

    try {
      for (const projectId of projectIds) {
        const ticketTypesResponse = await this.ticketTypesService.list({
          limit: 1, // We only need to know if there's at least one
          where: [
            {
              field: 'projectId',
              operator: '==',
              value: projectId,
            },
          ],
        });

        if (ticketTypesResponse.data.length === 0) {
          // Find project name
          const project = this.projects.find((p) => p.id === projectId);
          if (project) {
            projectsWithoutTicketTypes.push({
              id: projectId,
              name: project.name || 'Unknown Project',
            });
          }
        }
      }
    } catch (error) {
      // If there's an error, return empty array to avoid blocking users
      console.error('Error checking ticket types:', error);
    }

    return projectsWithoutTicketTypes;
  }

  // Navigate to ticket types page for a specific project
  navigateToTicketTypes(projectId: string) {
    this.navCtrl.navigateRoot(`/dashboard/settings/projects/${projectId}/ticket-types`);
  }

  // Get projects without ticket types for warning display (only for selected projects)
  getProjectsWithoutTicketTypesWarning(): { id: string; name: string }[] {
    return this.projectsWithoutTicketTypes;
  }

  // Check if a specific project has no ticket types
  hasProjectNoTicketTypes(projectId: string): boolean {
    return this.projectsWithoutTicketTypes.some((p) => p.id === projectId);
  }

  // Check if a project can be selected (has ticket types)
  canProjectBeSelected(projectId: string): boolean {
    return !this.hasProjectNoTicketTypes(projectId);
  }

  // Navigate to project creation
  navigateToProjectCreation() {
    this.navCtrl.navigateRoot('/dashboard/settings/project-list');
  }

  // Generate preview URL for disabled portals
  generatePreviewUrl(portal: Partial<Portal>): string {
    // Use stored preview URL if available, otherwise generate default
    if (portal.previewUrl) {
      return portal.previewUrl;
    }

    // For disabled portals, generate a default preview URL using the new format
    if (this.tenant?.subdomain) {
      return `https://portal.${this.tenant.subdomain}.unthread.io/${portal.id}`;
    }

    // Fallback to the old format if no tenant subdomain is available
    const baseUrl = window.location.origin;
    const previewPath = `/preview/portal/${portal.id}`;
    return `${baseUrl}${previewPath}`;
  }

  // Copy preview URL to clipboard
  async copyPreviewUrl(portal: Portal) {
    const previewUrl = this.generatePreviewUrl(portal);
    try {
      await navigator.clipboard.writeText(previewUrl);
      this.msgSrvc.show('Preview URL copied to clipboard');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = previewUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.msgSrvc.show('Preview URL copied to clipboard');
    }
  }

  // Test DNS connection for custom domain
  async testDnsConnection(portal: Partial<Portal>) {
    if (!portal.customDomain) {
      this.msgSrvc.show('No custom domain configured');
      return;
    }

    this.isDnsTesting = true;
    this.currentTestingPortal = portal as Portal;
    this.currentDnsStep = 0;

    try {
      // Simulate DNS test steps with delays
      for (let i = 0; i < this.dnsTestSteps.length; i++) {
        this.currentDnsStep = i;
        await new Promise((resolve) => setTimeout(resolve, 800));
      }

      const result = await this.apiService.postPromise<{
        success: boolean;
        message?: string;
        reason?: string;
        cnameRecords?: string[];
        expectedTarget?: string;
        error?: string;
      }>('/portals/test-dns', {
        customDomain: portal.customDomain,
        portalId: portal.id,
      });

      if (result.success) {
        this.msgSrvc.show('✅ DNS connection successful! Your custom domain is properly configured.');
      } else {
        this.msgSrvc.show(result.message || '❌ DNS connection failed. Please check your CNAME record configuration.', {
          duration: 0,
          buttons: [
            {
              text: '✕',
              role: 'cancel',
              handler: () => {
                // Toast will be dismissed automatically
              },
            },
          ],
        });
      }
    } catch (error: any) {
      if (error.status === 403) {
        this.msgSrvc.show('⚠️ Access denied. Please try refreshing the page and try again.');
      } else {
        this.msgSrvc.show('⚠️ Connection test failed. Please verify your DNS settings manually.');
      }
    } finally {
      this.isDnsTesting = false;
      this.currentTestingPortal = null;
      this.currentDnsStep = 0;
    }
  }

  // Test TLS certificate for custom domain
  async testTlsCertificate(portal: Partial<Portal>) {
    if (!portal.customDomain) {
      this.msgSrvc.show('No custom domain configured');
      return;
    }

    this.isTlsTesting = true;
    this.currentTestingPortal = portal as Portal;
    this.currentTlsStep = 0;

    try {
      // Simulate TLS test steps with delays
      for (let i = 0; i < this.tlsTestSteps.length; i++) {
        this.currentTlsStep = i;
        await new Promise((resolve) => setTimeout(resolve, 600));
      }

      const result = await this.apiService.postPromise<{
        success: boolean;
        message?: string;
        exists?: boolean;
        status?: string;
        sslStatus?: string;
        error?: string;
      }>('/portals/check-tls', {
        customDomain: portal.customDomain,
        portalId: portal.id,
      });

      if (result.success) {
        this.msgSrvc.show('✅ TLS certificate is valid and ready! Your portal can be published securely.');
      } else {
        let message = '❌ TLS certificate is not ready.';
        let isError = true;

        if (!result.exists) {
          message =
            '❌ TLS certificate has not been created yet. This usually takes a few minutes after DNS is configured.';
        } else if (result.error && result.error.includes('CNAME Configuration Error')) {
          // Show specific CNAME error message
          message = result.error;
        } else if (result.status !== 'active' || result.sslStatus !== 'active') {
          message = `🔄 TLS certificate is still being provisioned. Current status: Hostname ${result.status}, SSL ${result.sslStatus}.`;
          isError = false; // This is not an error, just in progress
        }

        if (isError) {
          this.msgSrvc.show(message, {
            duration: 0,
            buttons: [
              {
                text: '✕',
                role: 'cancel',
                handler: () => {
                  // Toast will be dismissed automatically
                },
              },
            ],
          });
        } else {
          this.msgSrvc.show(message); // Auto-dismiss for in-progress messages
        }
      }
    } catch (error: any) {
      if (error.status === 403) {
        this.msgSrvc.show('⚠️ Access denied. Please try refreshing the page and try again.');
      } else {
        this.msgSrvc.show('⚠️ TLS certificate check failed. Please try again later.');
      }
    } finally {
      this.isTlsTesting = false;
      this.currentTestingPortal = null;
      this.currentTlsStep = 0;
    }
  }

  // Generic copy to clipboard method
  async copyToClipboard(text: string, label?: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.msgSrvc.show(label ? `${label} copied to clipboard` : 'Copied to clipboard');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.msgSrvc.show(label ? `${label} copied to clipboard` : 'Copied to clipboard');
    }
  }

  async deletePortal(id: string) {
    const alert = await this.alertCtrl.create({
      header: 'Delete Portal',
      message: 'Are you sure you want to delete this portal? This action cannot be undone.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            this.isLoading = true;
            try {
              await this.portalsService.delete(id);
              this.portal = null; // Remove the portal from local state
              await this.startCreatePortal(); // start portal creation now that the existing portal has been deleted

              this.msgSrvc.show('Portal deleted successfully');
            } catch (err) {
              this.msgSrvc.showError(err);
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async publishPortal(id: string) {
    if (this.portal?.id !== id) return;
    const portal = this.portal;
    const customDomain = portal.customDomain || '';

    // Check if no custom domain is set and show warning modal
    if (!customDomain || customDomain.trim() === '') {
      const previewUrl = this.generatePreviewUrl(portal as Portal);

      const alert = await this.alertCtrl.create({
        header: 'No Custom Domain Configured',
        message: `
        <div style="text-align: left;">
          <p><strong>You have not configured a custom domain.</strong></p>
          <p>Your portal will be hosted publicly at:</p>
          <div style="
            display: inline-block;
            background: #f3f4f6;
            border-radius: 6px;
            padding: 8px 12px;
            font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
            font-size: 13px;
            color: #374151;
            margin: 8px 0;
            font-weight: 500;
          ">${previewUrl}</div>
          <p><strong>We highly recommend using a custom domain.</strong></p>
          <p>Do you wish to continue publishing to the preview domain only?</p>
        </div>
        `,
        buttons: [
          {
            text: 'No',
            role: 'cancel',
            cssClass: 'cancel-btn',
            handler: () => {
              // Do nothing, just dismiss
            },
          },
          {
            text: 'Yes',
            role: 'confirm',
            cssClass: 'publish-btn',
            handler: () => {
              // Continue with publish process without custom domain
              this.continuePublishWithoutCustomDomain(id, portal);
            },
          },
        ],
        cssClass: 'publish-portal-alert',
      });

      await alert.present();
      return;
    }

    // Continue with normal publish process for portals with custom domain
    await this.continuePublishWithCustomDomain(id, portal);
  }

  private async continuePublishWithoutCustomDomain(id: string, portal: Partial<Portal>) {
    // Start publishing process with step tracking
    this.isPublishing = true;
    this.currentTestingPortal = portal.id ? (portal as Portal) : null;
    this.currentPublishStep = 0;

    try {
      // Step 1: Validate ticket types
      this.currentPublishStep = 0;
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (portal.projectIds && portal.projectIds.length > 0) {
        const projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(portal.projectIds);
        if (projectsWithoutTicketTypes.length > 0) {
          this.isPublishing = false;
          this.currentTestingPortal = null;
          this.currentPublishStep = 0;

          const projectList = projectsWithoutTicketTypes
            .map((project) => `<li><strong>${project.name}</strong></li>`)
            .join('');

          const alert = await this.alertCtrl.create({
            header: 'Cannot Publish Portal',
            message: `
              <div style="text-align: left;">
                <p><strong>All enabled projects must have at least one ticket type before you can publish the portal.</strong></p>
                <p>The following projects don't have any ticket types:</p>
                <ul style="margin: 8px 0; padding-left: 20px;">
                  ${projectList}
                </ul>
                <p>Please create ticket types for these projects first, then try again.</p>
              </div>
            `,
            buttons: [
              {
                text: 'OK',
                role: 'cancel',
              },
              {
                text: 'Go to Ticket Types',
                handler: () => {
                  // Navigate to the first project's ticket types page
                  if (projectsWithoutTicketTypes.length > 0) {
                    this.navigateToTicketTypes(projectsWithoutTicketTypes[0].id);
                  }
                },
              },
            ],
          });

          await alert.present();
          return;
        }
      }

      // Step 2: Validate project configuration
      this.currentPublishStep = 1;
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Step 3: Verify portal requirements
      this.currentPublishStep = 2;
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Skip DNS and TLS checks for portals without custom domain
      // Step 4: Prepare to publish (skip DNS/TLS)
      this.currentPublishStep = 5;
      await new Promise((resolve) => setTimeout(resolve, 400));

      this.isPublishing = false;
      this.currentTestingPortal = null;
      this.currentPublishStep = 0;

      // Show publish confirmation for preview domain
      const previewUrl = this.generatePreviewUrl(portal as Portal);
      const alert = await this.alertCtrl.create({
        header: '',
        message: `
        <div style="
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 32px 24px 24px 24px;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.10);
          max-width: 480px;
          margin: 0 auto;
          text-align: left;
        ">
          <!-- Header with icon and title -->
          <div style="
            display: flex;
            align-items: center;
            margin-bottom: 24px;
            width: 100%;
          ">
            <div style="
              display: flex;
              align-items: center;
              justify-content: center;
              width: 48px;
              height: 48px;
              background: #16a34a;
              border-radius: 50%;
              margin-right: 16px;
              flex-shrink: 0;
            ">
              <span style="
                font-size: 24px;
                color: white;
                font-weight: bold;
              ">✓</span>
            </div>
            <div>
              <h2 style="
                margin: 0;
                font-size: 20px;
                font-weight: 600;
                color: #1f2937;
                line-height: 1.2;
              ">You are ready to publish!</h2>
            </div>
          </div>

          <!-- Content -->
          <div style="
            width: 100%;
            margin-bottom: 24px;
          ">
            <p style="
              margin: 0 0 16px 0;
              font-size: 16px;
              color: #374151;
              line-height: 1.5;
              font-weight: 500;
            ">Are you sure you want to publish this portal?</p>
            <p style="
              margin: 0;
              font-size: 14px;
              color: #6b7280;
              line-height: 1.5;
            ">
              Once you do so, anyone who visits
              <span style="
                display: inline-block;
                background: #f3f4f6;
                border-radius: 6px;
                padding: 4px 8px;
                font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
                font-size: 13px;
                color: #374151;
                margin: 0 4px;
                font-weight: 500;
              ">${previewUrl}</span>
              will be able to access relevant knowledge bases and resources.
            </p>
            <div style="
              margin-top: 16px;
              padding: 12px;
              background: #fef3c7;
              border-radius: 6px;
              border-left: 3px solid #f59e0b;
              font-size: 14px;
              color: #92400e;
            ">
              <strong>Note:</strong> This portal will be published to the preview domain. Consider setting up a custom domain for production use.
            </div>
          </div>
        </div>
      `,
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'cancel-btn',
            handler: () => {
              // Do nothing, just dismiss
            },
          },
          {
            text: 'Publish',
            role: 'confirm',
            cssClass: 'publish-btn',
            handler: async () => {
              this.isLoading = true;
              try {
                await this.portalsService.update(id, { isEnabled: true });
                if (this.portal) {
                  this.portal.isEnabled = true;
                }
                this.msgSrvc.show('Portal published successfully');
              } catch (err) {
                this.msgSrvc.showError(err);
              } finally {
                this.isLoading = false;
              }
            },
          },
        ],
        cssClass: 'publish-portal-alert',
      });

      await alert.present();
    } catch (error) {
      this.isPublishing = false;
      this.currentTestingPortal = null;
      this.currentPublishStep = 0;
      this.msgSrvc.showError('An unexpected error occurred during publishing.');
    }
  }

  private async continuePublishWithCustomDomain(id: string, portal: Partial<Portal>) {
    const customDomain = portal.customDomain || '';

    // Start publishing process with step tracking
    this.isPublishing = true;
    this.currentTestingPortal = portal.id ? (portal as Portal) : null;
    this.currentPublishStep = 0;

    try {
      // Step 1: Validate ticket types
      this.currentPublishStep = 0;
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (portal.projectIds && portal.projectIds.length > 0) {
        const projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(portal.projectIds);
        if (projectsWithoutTicketTypes.length > 0) {
          this.isPublishing = false;
          this.currentTestingPortal = null;
          this.currentPublishStep = 0;

          const projectList = projectsWithoutTicketTypes
            .map((project) => `<li><strong>${project.name}</strong></li>`)
            .join('');

          const alert = await this.alertCtrl.create({
            header: 'Cannot Publish Portal',
            message: `
              <div style="text-align: left;">
                <p><strong>All enabled projects must have at least one ticket type before you can publish the portal.</strong></p>
                <p>The following projects don't have any ticket types:</p>
                <ul style="margin: 8px 0; padding-left: 20px;">
                  ${projectList}
                </ul>
                <p>Please create ticket types for these projects first, then try again.</p>
              </div>
            `,
            buttons: [
              {
                text: 'OK',
                role: 'cancel',
              },
              {
                text: 'Go to Ticket Types',
                handler: () => {
                  // Navigate to the first project's ticket types page
                  if (projectsWithoutTicketTypes.length > 0) {
                    this.navigateToTicketTypes(projectsWithoutTicketTypes[0].id);
                  }
                },
              },
            ],
          });

          await alert.present();
          return;
        }
      }

      // Step 2: Validate project configuration
      this.currentPublishStep = 1;
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Step 3: Verify portal requirements
      this.currentPublishStep = 2;
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Step 4: Test DNS configuration
      this.currentPublishStep = 3;
      await new Promise((resolve) => setTimeout(resolve, 800));

      let dnsResult: { success: boolean; message?: string } | undefined;
      try {
        dnsResult = await this.apiService.postPromise<{
          success: boolean;
          message?: string;
          reason?: string;
          cnameRecords?: string[];
          expectedTarget?: string;
          error?: string;
        }>('/portals/test-dns', {
          customDomain: portal.customDomain,
          portalId: portal.id,
        });
      } catch (error: any) {
        this.isPublishing = false;
        this.currentTestingPortal = null;
        this.currentPublishStep = 0;
        await this.alertCtrl
          .create({
            header: 'DNS Test Failed',
            message: 'Could not verify DNS configuration. Please try again or check your DNS settings.',
            buttons: ['OK'],
          })
          .then((alert) => alert.present());
        return;
      }

      if (!dnsResult?.success) {
        this.isPublishing = false;
        this.currentTestingPortal = null;
        this.currentPublishStep = 0;
        await this.alertCtrl
          .create({
            header: 'Cannot Publish Portal',
            message: `
            <div style="text-align: left;">
              <strong>You cannot publish this portal until your DNS is properly configured.</strong><br><br>
              ${dnsResult?.message || `❌ CNAME Configuration Error: Your domain ${customDomain} does not have a CNAME record pointing to portal.unthread.io. Please add a CNAME record in your DNS settings.`}<br><br>
              <ul style="margin-top: 8px; margin-bottom: 8px;">
                <li>Make sure your subdomain <code>${customDomain}</code> points to <code>portal.unthread.io</code>.</li>
                <li>DNS changes may take up to 24 hours to propagate.</li>
                <li>After updating your DNS, try publishing again.</li>
              </ul>
            </div>
          `,
            buttons: ['OK'],
          })
          .then((alert) => alert.present());
        return;
      }

      // Step 5: Check TLS certificate status
      this.currentPublishStep = 4;
      await new Promise((resolve) => setTimeout(resolve, 600));

      let tlsResult:
        | { success: boolean; message?: string; exists?: boolean; status?: string; sslStatus?: string }
        | undefined;
      try {
        tlsResult = await this.apiService.postPromise<{
          success: boolean;
          message?: string;
          exists?: boolean;
          status?: string;
          sslStatus?: string;
          error?: string;
        }>('/portals/check-tls', {
          customDomain: portal.customDomain,
          portalId: portal.id,
        });
      } catch (error: any) {
        this.isPublishing = false;
        this.currentTestingPortal = null;
        this.currentPublishStep = 0;
        await this.alertCtrl
          .create({
            header: 'TLS Certificate Check Failed',
            message: 'Could not verify TLS certificate status. Please try again.',
            buttons: ['OK'],
          })
          .then((alert) => alert.present());
        return;
      }

      if (!tlsResult?.success) {
        this.isPublishing = false;
        this.currentTestingPortal = null;
        this.currentPublishStep = 0;
        let tlsMessage = tlsResult?.message || 'TLS certificate is not ready';

        if (!tlsResult?.exists) {
          tlsMessage =
            'TLS certificate has not been created yet. This usually takes a few minutes after DNS is configured.';
        } else if (tlsResult?.message && tlsResult.message.includes('CNAME Configuration Error')) {
          // Show specific CNAME error message
          tlsMessage = tlsResult.message;
        } else if (tlsResult?.status !== 'active' || tlsResult?.sslStatus !== 'active') {
          tlsMessage = `TLS certificate is still being provisioned. Current status: Hostname ${tlsResult?.status}, SSL ${tlsResult?.sslStatus}. This can take up to 10 minutes.`;
        }

        await this.alertCtrl
          .create({
            header: 'Cannot Publish Portal',
            message: `
            <div style="text-align: left;">
              <strong>TLS certificate not ready.</strong><br><br>
              ${tlsMessage}<br><br>
              <div style="margin-top: 12px; padding: 8px; background: #f8f9fa; border-radius: 6px; border-left: 3px solid #007bff; font-size: 14px;">
                <strong>Note:</strong> Unthread handles TLS certificates for you. If the certificate is not ready in 10 minutes, <a href="mailto:jake@unthread.io" style="color: #007bff;">contact us</a> for assistance.
              </div>
            </div>
          `,
            buttons: ['OK'],
          })
          .then((alert) => alert.present());
        return;
      }

      // Step 6: Prepare to publish
      this.currentPublishStep = 5;
      await new Promise((resolve) => setTimeout(resolve, 400));

      this.isPublishing = false;
      this.currentTestingPortal = null;
      this.currentPublishStep = 0;

      // Use alert for publish confirmation with similar styling
      const alert = await this.alertCtrl.create({
        header: '',
        message: `
        <div style="
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 32px 24px 24px 24px;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.10);
          max-width: 480px;
          margin: 0 auto;
          text-align: left;
        ">
          <!-- Header with icon and title -->
          <div style="
            display: flex;
            align-items: center;
            margin-bottom: 24px;
            width: 100%;
          ">
            <div style="
              display: flex;
              align-items: center;
              justify-content: center;
              width: 48px;
              height: 48px;
              background: #16a34a;
              border-radius: 50%;
              margin-right: 16px;
              flex-shrink: 0;
            ">
              <span style="
                font-size: 24px;
                color: white;
                font-weight: bold;
              ">✓</span>
            </div>
            <div>
              <h2 style="
                margin: 0;
                font-size: 20px;
                font-weight: 600;
                color: #1f2937;
                line-height: 1.2;
              ">You are ready to publish!</h2>
            </div>
          </div>

          <!-- Content -->
          <div style="
            width: 100%;
            margin-bottom: 24px;
          ">
            <p style="
              margin: 0 0 16px 0;
              font-size: 16px;
              color: #374151;
              line-height: 1.5;
              font-weight: 500;
            ">Are you sure you want to publish this portal?</p>
            <p style="
              margin: 0;
              font-size: 14px;
              color: #6b7280;
              line-height: 1.5;
            ">
              Once you do so, anyone who visits
              <span style="
                display: inline-block;
                background: #f3f4f6;
                border-radius: 6px;
                padding: 4px 8px;
                font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
                font-size: 13px;
                color: #374151;
                margin: 0 4px;
                font-weight: 500;
              ">${customDomain}</span>
              will be able to access relevant knowledge bases and resources.
            </p>
          </div>
        </div>
      `,
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'cancel-btn',
            handler: () => {
              // Do nothing, just dismiss
            },
          },
          {
            text: 'Publish',
            role: 'confirm',
            cssClass: 'publish-btn',
            handler: async () => {
              this.isLoading = true;
              try {
                await this.portalsService.update(id, { isEnabled: true });
                if (this.portal) {
                  this.portal.isEnabled = true;
                }
                this.msgSrvc.show('Portal published successfully');
              } catch (err) {
                this.msgSrvc.showError(err);
              } finally {
                this.isLoading = false;
              }
            },
          },
        ],
        cssClass: 'publish-portal-alert',
      });

      await alert.present();
    } catch (error) {
      this.isPublishing = false;
      this.currentTestingPortal = null;
      this.currentPublishStep = 0;
      this.msgSrvc.showError('An unexpected error occurred during publishing.');
    }
  }

  async disablePortal(id: string) {
    const alert = await this.alertCtrl.create({
      header: 'Disable Portal',
      message: 'Are you sure you want to disable this portal? Users will no longer be able to access it.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Disable',
          role: 'destructive',
          handler: async () => {
            this.isLoading = true;
            try {
              await this.portalsService.update(id, { isEnabled: false });

              if (this.portal) {
                this.portal.isEnabled = false;
              }
            } catch (err) {
              this.msgSrvc.showError(err);
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async showLogoModal(logoUrl: string) {
    const alert = await this.alertCtrl.create({
      header: 'Portal Logo',
      message: `<div style="text-align: center; padding: 20px;">
      <img src="${logoUrl}" style="max-width: 100%; max-height: 60vh; width: auto; height: auto; object-fit: contain; border-radius: 8px;" />
    </div>`,
      buttons: ['Close'],
      cssClass: 'logo-modal',
    });

    await alert.present();
  }

  // Domain validation method
  validateDomain(domain: string): string {
    if (!domain) {
      return '';
    }

    // Remove protocol if present
    const cleanDomain = domain.replace(/^https?:\/\//, '');

    // Basic domain regex pattern
    const domainPattern =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

    if (!domainPattern.test(cleanDomain)) {
      const exampleDomain = this.tenant?.subdomain
        ? `support.${this.tenant?.subdomain}.com`
        : 'support.yourcompany.com';
      return `Please enter a valid domain (e.g., ${exampleDomain})`;
    }

    // Check for common invalid domains
    const invalidDomains = ['localhost', '127.0.0.1', '0.0.0.0'];
    if (invalidDomains.includes(cleanDomain.toLowerCase())) {
      return 'Please enter a valid domain name';
    }

    return '';
  }

  // Check if domain is valid (returns boolean)
  isValidDomain(domain: string): boolean {
    return this.validateDomain(domain) === '';
  }

  // Handle project checkbox changes
  async onProjectCheckboxChange(projectId: string, event: CustomEvent<{ checked: boolean }>) {
    // Prevent projects without ticket types from being selected
    if (this.hasProjectNoTicketTypes(projectId)) {
      return;
    }

    if (event.detail.checked) {
      if (!this.portalForm.selectedProjectIds.includes(projectId)) {
        this.portalForm.selectedProjectIds.push(projectId);
      }
    } else {
      this.portalForm.selectedProjectIds = this.portalForm.selectedProjectIds.filter((id) => id !== projectId);
    }
  }
}
